import type { AccessibilitySettings, AudioSettings } from "@app-types/core";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type { AccessibilitySettings, AudioSettings } from "@app-types/core";

interface SettingsState {
  accessibility: AccessibilitySettings;
  audio: AudioSettings;
  setAccessibility: (patch: Partial<AccessibilitySettings>) => void;
  setAudio: (patch: Partial<AudioSettings>) => void;
}

const prefersReducedMotion =
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      accessibility: {
        largeText: false,
        highContrast: false,
        reducedMotion: Boolean(prefersReducedMotion),
        captions: false,
        voiceNarration: false,
      },
      audio: {
        masterVolume: 1,
        musicVolume: 0.6,
        sfxVolume: 0.8,
        muted: false,
      },
      setAccessibility: (patch) =>
        set((state) => ({ accessibility: { ...state.accessibility, ...patch } })),
      setAudio: (patch) => set((state) => ({ audio: { ...state.audio, ...patch } })),
    }),
    {
      name: "the-quest:settings",
      merge: (persisted, current) => {
        const persistedState = persisted as Partial<SettingsState> | undefined;
        return {
          ...current,
          ...persistedState,
          accessibility: { ...current.accessibility, ...persistedState?.accessibility },
          audio: { ...current.audio, ...persistedState?.audio },
        };
      },
    },
  ),
);
